import React, { useState, useEffect, useCallback } from 'react';
import { faqAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';

const CATEGORY_ICONS = ['help', 'settings', 'lock', 'wifi', 'mail', 'monitor', 'layers', 'shield', 'alert', 'info', 'tag', 'star'];

const s = {
  wrap:  { padding: '4px 0 32px' },
  tabs:  { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e5e7eb' },
  tab:   (a) => ({ padding: '8px 18px', cursor: 'pointer', borderRadius: '6px 6px 0 0', fontWeight: 600, fontSize: 13, background: a ? '#fff' : 'transparent', color: a ? '#111' : '#6b7280', borderBottom: a ? '2px solid var(--accent)' : '2px solid transparent' }),
  card:  { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' },
  th:    { fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', background: '#F8FAFF' },
  td:    { fontSize: 13, color: '#374151', padding: '11px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  btn:   (v) => ({ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : '#F1F5F9', color: v === 'primary' ? '#fff' : v === 'danger' ? '#EF4444' : '#374151' }),
  input: { width: '100%', padding: '9px 11px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: '#FAFAFA', color: '#1A202C' },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 },
};

/* ── Category pill ── */
function CatBadge({ name, color = '#3B5BDB' }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: `${color}18`, color, whiteSpace: 'nowrap' }}>{name}</span>
  );
}

/* ── Status toggle pill ── */
function PubBadge({ published }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: published ? '#ECFDF5' : '#F9FAFB', color: published ? '#059669' : '#9CA3AF' }}>
      {published ? 'Published' : 'Draft'}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════
   CATEGORY MODAL
══════════════════════════════════════════════════════════ */
function CategoryModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', description: '', icon: 'help', sort_order: 0, ...initial });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast('Category name is required', 'error'); return; }
    setSaving(true);
    try {
      const result = initial?.id
        ? await faqAPI.updateCategory(initial.id, form)
        : await faqAPI.createCategory(form);
      onSave(result);
    } catch (err) {
      toast(err.message || 'Failed to save category', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, padding: '28px 28px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{initial?.id ? 'Edit Category' : 'New Category'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icon name="close" size={18} color="#9CA3AF" /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>Name *</label>
            <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. DLP - Data Loss Prevention" />
          </div>
          <div>
            <label style={s.label}>Description</label>
            <input style={s.input} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description of this category" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={s.label}>Icon</label>
              <select style={s.input} value={form.icon || 'help'} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}>
                {CATEGORY_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Sort Order</label>
              <input type="number" style={s.input} value={form.sort_order || 0} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} min={0} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button style={s.btn('default')} onClick={onClose}>Cancel</button>
          <button style={s.btn('primary')} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Category'}</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   FAQ MODAL (Create / Edit)
══════════════════════════════════════════════════════════ */
function FAQModal({ initial, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    category_id: '',
    question: '',
    answer: '',
    tags: '',
    is_published: true,
    ...initial,
    tags: initial?.tags ? (Array.isArray(initial.tags) ? initial.tags.join(', ') : initial.tags) : '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.question.trim()) { toast('Question is required', 'error'); return; }
    if (!form.answer.trim())   { toast('Answer is required',   'error'); return; }
    setSaving(true);
    const payload = {
      ...form,
      category_id: form.category_id || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };
    try {
      const result = initial?.id
        ? await faqAPI.update(initial.id, payload)
        : await faqAPI.create(payload);
      onSave(result);
    } catch (err) {
      toast(err.message || 'Failed to save FAQ', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 28px 16px', borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{initial?.id ? 'Edit FAQ' : 'New FAQ'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icon name="close" size={18} color="#9CA3AF" /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>Category</label>
            <select style={s.input} value={form.category_id || ''} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
              <option value="">-- No Category --</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Question *</label>
            <input style={s.input} value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} placeholder="e.g. How do I reset my password?" />
          </div>
          <div>
            <label style={s.label}>Answer *</label>
            <textarea style={{ ...s.input, resize: 'vertical', minHeight: 200, lineHeight: 1.6 }} value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} placeholder="Provide a detailed, step-by-step answer..." />
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Supports line breaks. Use **bold** for emphasis in markdown-rendered views.</div>
          </div>
          <div>
            <label style={s.label}>Tags (comma-separated)</label>
            <input style={s.input} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. password, reset, account, login" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="faq-published" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="faq-published" style={{ fontSize: 13, color: '#374151', cursor: 'pointer', fontWeight: 600 }}>Published (visible to users)</label>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 28px 20px', borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
          <button style={s.btn('default')} onClick={onClose}>Cancel</button>
          <button style={s.btn('primary')} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : initial?.id ? 'Save Changes' : 'Create FAQ'}</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CATEGORIES TAB
══════════════════════════════════════════════════════════ */
function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | cat-object

  const load = useCallback(async () => {
    setLoading(true);
    try { setCats(await faqAPI.listCategoriesAll()); }
    catch (err) { toast(err.message || 'Failed to load categories', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = (saved) => {
    setCats(prev => {
      const exists = prev.find(c => c.id === saved.id);
      return exists ? prev.map(c => c.id === saved.id ? { ...c, ...saved } : c) : [...prev, saved];
    });
    setModal(null);
    toast(modal?.id ? 'Category updated' : 'Category created');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? FAQs in it will become uncategorised.')) return;
    try {
      await faqAPI.deleteCategory(id);
      setCats(prev => prev.filter(c => c.id !== id));
      toast('Category deleted');
    } catch (err) { toast(err.message || 'Delete failed', 'error'); }
  };

  const handleToggle = async (cat) => {
    try {
      const updated = await faqAPI.updateCategory(cat.id, { is_active: !cat.is_active });
      setCats(prev => prev.map(c => c.id === cat.id ? { ...c, ...updated } : c));
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: '#6B7280' }}>{cats.length} categories</div>
        <button style={s.btn('primary')} onClick={() => setModal('create')}>
          <Icon name="plus" size={13} color="#fff" /> Add Category
        </button>
      </div>

      <div style={s.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Category', 'Description', 'FAQs', 'Order', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ ...s.th, textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#9CA3AF', padding: '32px 0' }}>Loading...</td></tr>
            ) : cats.length === 0 ? (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#9CA3AF', padding: '32px 0' }}>No categories yet</td></tr>
            ) : cats.map(cat => (
              <tr key={cat.id}>
                <td style={s.td}>
                  <div style={{ fontWeight: 700, color: '#1A202C' }}>{cat.name}</div>
                </td>
                <td style={{ ...s.td, maxWidth: 220, color: '#6B7280' }}>{cat.description || '--'}</td>
                <td style={s.td}><span style={{ fontWeight: 700, color: 'var(--accent)' }}>{cat.faq_count || 0}</span></td>
                <td style={s.td}>{cat.sort_order}</td>
                <td style={s.td}>
                  <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: cat.is_active ? '#ECFDF5' : '#F9FAFB', color: cat.is_active ? '#059669' : '#9CA3AF' }}>
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.btn('default')} onClick={() => setModal(cat)} title="Edit">
                      <Icon name="edit" size={13} color="#374151" />
                    </button>
                    <button style={s.btn('default')} onClick={() => handleToggle(cat)} title={cat.is_active ? 'Deactivate' : 'Activate'}>
                      <Icon name={cat.is_active ? 'eye' : 'eye'} size={13} color={cat.is_active ? '#059669' : '#9CA3AF'} />
                      {cat.is_active ? 'Hide' : 'Show'}
                    </button>
                    <button style={s.btn('danger')} onClick={() => handleDelete(cat.id)} title="Delete">
                      <Icon name="trash" size={13} color="#EF4444" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <CategoryModal
          initial={modal === 'create' ? undefined : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   FAQs TAB
══════════════════════════════════════════════════════════ */
function FAQsTab() {
  const [faqs, setFaqs]       = useState([]);
  const [cats, setCats]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterPub, setFilterPub] = useState('');
  const [modal, setModal]     = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [faqRes, catRes] = await Promise.all([
        faqAPI.list({ limit: 500 }),
        faqAPI.listCategoriesAll(),
      ]);
      setFaqs(faqRes.faqs || []);
      setCats(catRes);
    } catch (err) { toast(err.message || 'Failed to load FAQs', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-side filter
  const filtered = faqs.filter(f => {
    if (filterCat && String(f.category_id) !== String(filterCat)) return false;
    if (filterPub === 'published' && !f.is_published) return false;
    if (filterPub === 'draft'     &&  f.is_published) return false;
    if (search && !f.question.toLowerCase().includes(search.toLowerCase()) && !f.answer.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const catMap = Object.fromEntries(cats.map(c => [c.id, c]));
  const CAT_COLORS = ['#3B5BDB','#059669','#D97706','#7C3AED','#E53E3E','#0EA5E9','#F97316','#10B981'];
  function catColor(id) { return CAT_COLORS[(id - 1) % CAT_COLORS.length] || '#3B5BDB'; }

  const handleSave = (saved) => {
    setFaqs(prev => {
      const exists = prev.find(f => f.id === saved.id);
      return exists ? prev.map(f => f.id === saved.id ? { ...f, ...saved } : f) : [saved, ...prev];
    });
    setModal(null);
    toast(modal?.id ? 'FAQ updated' : 'FAQ created');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ? This cannot be undone.')) return;
    try {
      await faqAPI.delete(id);
      setFaqs(prev => prev.filter(f => f.id !== id));
      toast('FAQ deleted');
    } catch (err) { toast(err.message || 'Delete failed', 'error'); }
  };

  const handleTogglePublish = async (faq) => {
    try {
      const updated = await faqAPI.update(faq.id, { is_published: !faq.is_published });
      setFaqs(prev => prev.map(f => f.id === faq.id ? { ...f, ...updated } : f));
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <Icon name="search" size={13} color="#9CA3AF" />
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search FAQs..." style={{ ...s.input, paddingLeft: 30 }} />
        </div>
        <select style={{ ...s.input, width: 'auto', minWidth: 180 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All Categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...s.input, width: 'auto', minWidth: 130 }} value={filterPub} onChange={e => setFilterPub(e.target.value)}>
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#6B7280', alignSelf: 'center' }}>{filtered.length} of {faqs.length} FAQs</span>
          <button style={s.btn('primary')} onClick={() => setModal('create')}>
            <Icon name="plus" size={13} color="#fff" /> Add FAQ
          </button>
        </div>
      </div>

      {/* FAQ list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>
            {search || filterCat || filterPub ? 'No FAQs match the current filters.' : 'No FAQs yet. Click "Add FAQ" to create the first one.'}
          </div>
        ) : filtered.map(faq => {
          const cat = catMap[faq.category_id];
          const isOpen = expanded === faq.id;
          return (
            <div key={faq.id} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${isOpen ? 'var(--accent)' : '#E5E7EB'}`, overflow: 'hidden', transition: 'border-color 0.15s' }}>
              {/* Question row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '13px 16px', cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : faq.id)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    {cat && <CatBadge name={cat.name} color={catColor(faq.category_id)} />}
                    <PubBadge published={faq.is_published} />
                    {faq.view_count > 0 && (
                      <span style={{ fontSize: 11, color: '#9CA3AF' }}>{faq.view_count} views</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1A202C', lineHeight: 1.4 }}>{faq.question}</div>
                  {!isOpen && (
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                      {faq.answer.slice(0, 120)}{faq.answer.length > 120 ? '...' : ''}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  <button style={s.btn('default')} onClick={e => { e.stopPropagation(); setModal(faq); }} title="Edit">
                    <Icon name="edit" size={12} color="#374151" /> Edit
                  </button>
                  <button style={{ ...s.btn('default'), color: faq.is_published ? '#6B7280' : '#059669' }} onClick={e => { e.stopPropagation(); handleTogglePublish(faq); }} title={faq.is_published ? 'Unpublish' : 'Publish'}>
                    {faq.is_published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button style={s.btn('danger')} onClick={e => { e.stopPropagation(); handleDelete(faq.id); }} title="Delete">
                    <Icon name="trash" size={12} color="#EF4444" />
                  </button>
                  <span style={{ color: '#9CA3AF', fontSize: 14, cursor: 'pointer' }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Expanded answer */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #F1F5F9', padding: '14px 16px', background: '#F8FAFF' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Answer</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{faq.answer}</div>
                  {faq.tags?.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase' }}>Tags:</span>
                      {faq.tags.map((tag, i) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: 10, background: '#EEF2FF', color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>{tag}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', gap: 16, fontSize: 11, color: '#9CA3AF' }}>
                    <span>Views: {faq.view_count || 0}</span>
                    <span>Helpful: {faq.helpful_yes || 0}</span>
                    <span>Not helpful: {faq.helpful_no || 0}</span>
                    <span>Created: {faq.created_at ? new Date(faq.created_at).toLocaleDateString() : '-'}</span>
                    <span>Updated: {faq.updated_at ? new Date(faq.updated_at).toLocaleDateString() : '-'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modal && (
        <FAQModal
          initial={modal === 'create' ? undefined : modal}
          categories={cats}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ROOT EXPORT
══════════════════════════════════════════════════════════ */
export default function FAQAdmin() {
  const [tab, setTab] = useState('FAQs');

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#1A202C', letterSpacing: '-0.3px' }}>Knowledge Base & FAQs</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>Manage FAQ categories and articles. The AI Assistant uses this knowledge base to answer user questions.</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {['FAQs', 'Categories'].map(t => (
          <button key={t} style={s.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'FAQs'       && <FAQsTab />}
      {tab === 'Categories' && <CategoriesTab />}
    </div>
  );
}
